import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Modal, Button } from 'react-bootstrap';
import { HiChevronDown } from 'react-icons/hi2';
import {
  pageview,
  updateAnalyticsConsent,
} from '../analytics';

const COOKIE_CATEGORIES = {
  necessary: {
    title: 'Strictly Necessary',
    description:
      'These cookies are essential for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website. These cookies do not store any personally identifiable information.',
    required: true,
    cookies: [
      {
        name: 'cookie_preferences',
        duration: '1 year',
        description:
          'Stores your cookie consent choices so we can respect them on subsequent visits. It does not collect personal data.',
      },
      {
        name: 'store_info',
        duration: '1 year',
        description:
          'Keeps essential site and session information required for core features such as navigation and secure areas to work correctly.',
      },
    ],
  },
  functional: {
    title: 'Functional',
    description:
      'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers.',
    required: false,
    cookies: [
      {
        name: 'personalise_content',
        duration: '1 year',
        description:
          'Allows the site to remember choices that personalize content for you, such as preferred layouts or saved preferences.',
      },
      {
        name: 'select_personalised_content',
        duration: '1 year',
        description:
          'Helps select and display personalized content based on how you use the website.',
      },
    ],
  },
  analytics: {
    title: 'Analytics',
    description:
      'These cookies help us understand how visitors interact with our website. All information these cookies collect is aggregated and anonymous.',
    required: false,
    cookies: [
      {
        name: 'measure_content_performance',
        duration: '1 year',
        description:
          'Measures how content performs so we can understand which pages and features are most useful.',
      },
      {
        name: 'audience_insight',
        duration: '1 year',
        description:
          'Collects aggregated insights about audience behavior to help improve the overall site experience.',
      },
      {
        name: 'improve_services',
        duration: '1 year',
        description:
          'Supports analysis used to improve our services, features, and site reliability.',
      },
    ],
  },
  marketing: {
    title: 'Marketing',
    description:
      'These cookies are used to track visitors across websites. They are set by our advertising partners and help us deliver more relevant advertisements.',
    required: false,
    cookies: [
      {
        name: 'select_advertising',
        duration: '1 year',
        description:
          'Helps select advertisements that are more relevant to your interests.',
      },
      {
        name: 'personalised_ads',
        duration: '1 year',
        description:
          'Enables personalized advertising based on your activity on this and other sites.',
      },
      {
        name: 'select_personalised_ads',
        duration: '1 year',
        description:
          'Used to choose which personalized ads to show you across advertising partners.',
      },
      {
        name: 'measure_ad_performance',
        duration: '1 year',
        description:
          'Measures advertising campaign performance so we can understand ad effectiveness.',
      },
    ],
  },
};

const getDefaultCategoryPrefs = () =>
  Object.keys(COOKIE_CATEGORIES).reduce(
    (acc, category) => ({
      ...acc,
      [category]: COOKIE_CATEGORIES[category].required,
    }),
    {}
  );

const deriveCategoryPrefs = (cookiePrefs) =>
  Object.keys(COOKIE_CATEGORIES).reduce((acc, category) => {
    const data = COOKIE_CATEGORIES[category];
    if (data.required) {
      acc[category] = true;
      return acc;
    }
    acc[category] = data.cookies.every((cookie) => cookiePrefs[cookie.name] === true);
    return acc;
  }, {});

const SimpleBanner = ({ onAcceptAll, onRejectAll, onShowOptions }) => {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'white',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        padding: '20px',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxWidth: '600px',
        width: '90%',
        borderRadius: '8px 8px 0 0',
      }}
    >
      <div style={{ fontSize: '16px', color: '#333', textAlign: 'center', lineHeight: '1.5' }}>
        We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.
        <a
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#C93312', marginLeft: '8px', fontWeight: '500' }}
        >
          Learn more
        </a>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outline-danger"
          onClick={onRejectAll}
          style={{
            borderColor: '#000',
            color: '#000',
          }}
        >
          Reject All
        </Button>
        <Button
          variant="outline-primary"
          onClick={onShowOptions}
          style={{
            borderColor: '#C93312',
            color: '#C93312',
          }}
        >
          Cookie Settings
        </Button>
        <Button
          variant="primary"
          onClick={onAcceptAll}
          style={{
            backgroundColor: '#C93312',
            borderColor: '#C93312',
          }}
        >
          Accept All
        </Button>
      </div>
    </div>
  );
};

const CookieCategoryPanel = ({ category, data, enabled, onToggle, expanded, onToggleExpand }) => {
  return (
    <div className="cookie-category-panel">
      <div className="cookie-category-panel__header">
        <button
          type="button"
          className="cookie-category-panel__toggle"
          onClick={() => onToggleExpand(category)}
          aria-expanded={expanded}
          aria-controls={`cookie-category-${category}`}
        >
          <HiChevronDown
            className={`cookie-category-panel__chevron${expanded ? ' is-expanded' : ''}`}
            aria-hidden="true"
          />
          <span className="cookie-category-panel__title">{data.title}</span>
        </button>
        {data.required ? (
          <span className="cookie-category-panel__always-active">Always Active</span>
        ) : (
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              checked={enabled}
              onChange={() => onToggle(category)}
              style={{ width: '40px', height: '20px' }}
              aria-label={`${data.title} cookies`}
            />
          </div>
        )}
      </div>

      {expanded && (
        <div id={`cookie-category-${category}`} className="cookie-category-panel__body">
          <p className="cookie-category-panel__description">{data.description}</p>
          <div className="cookie-detail-list">
            {data.cookies.map((cookie) => (
              <div key={cookie.name} className="cookie-detail-item">
                <div className="cookie-detail-row">
                  <span className="cookie-detail-label">Cookie file</span>
                  <span className="cookie-detail-value">{cookie.name}</span>
                </div>
                <div className="cookie-detail-row">
                  <span className="cookie-detail-label">Duration</span>
                  <span className="cookie-detail-value">{cookie.duration}</span>
                </div>
                <div className="cookie-detail-row">
                  <span className="cookie-detail-label">Description</span>
                  <span className="cookie-detail-value">{cookie.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CookiePreferences = ({ show: externalShow, onClose, showBanner = false }) => {
  const [internalShow, setInternalShow] = useState(() => !Cookies.get('cookie_preferences'));
  const [showDetailedSettings, setShowDetailedSettings] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(() => ({ necessary: true }));
  const [preferences, setPreferences] = useState(() => {
    const savedPrefs = Cookies.get('cookie_preferences');
    if (savedPrefs) {
      try {
        return deriveCategoryPrefs(JSON.parse(savedPrefs));
      } catch {
        return getDefaultCategoryPrefs();
      }
    }
    return getDefaultCategoryPrefs();
  });

  useEffect(() => {
    const syncPreferences = (event) => {
      if (!event.detail) return;
      setPreferences(event.detail);
      setInternalShow(false);
    };
    window.addEventListener('cookie-preferences-updated', syncPreferences);
    return () =>
      window.removeEventListener('cookie-preferences-updated', syncPreferences);
  }, []);

  const savePreferences = (newPrefs) => {
    const cookiePrefs = {};
    Object.entries(COOKIE_CATEGORIES).forEach(([category, data]) => {
      if (newPrefs[category] || data.required) {
        data.cookies.forEach((cookie) => {
          cookiePrefs[cookie.name] = true;
        });
      }
    });

    Cookies.set('cookie_preferences', JSON.stringify(cookiePrefs), {
      expires: 365,
      sameSite: 'strict',
      secure: true,
    });
    updateAnalyticsConsent(Boolean(newPrefs.analytics));
    if (newPrefs.analytics) {
      pageview(window.location.pathname + window.location.search);
    }
    window.dispatchEvent(
      new CustomEvent('cookie-preferences-updated', { detail: newPrefs })
    );
    setPreferences(newPrefs);
    setInternalShow(false);
    setShowDetailedSettings(false);
    if (onClose) {
      onClose();
    }
  };

  const handleAcceptAll = () => {
    const allPrefs = Object.keys(COOKIE_CATEGORIES).reduce(
      (acc, category) => ({
        ...acc,
        [category]: true,
      }),
      {}
    );
    savePreferences(allPrefs);
  };

  const handleRejectAll = () => {
    savePreferences(getDefaultCategoryPrefs());
  };

  const handleShowOptions = () => {
    setShowDetailedSettings(true);
  };

  const handleToggleCategory = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleToggleExpand = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (showBanner && !showDetailedSettings && internalShow) {
    return (
      <SimpleBanner
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
        onShowOptions={handleShowOptions}
      />
    );
  }

  return (
    <Modal
      show={showDetailedSettings || externalShow || false}
      onHide={() => {
        setShowDetailedSettings(false);
        if (onClose) onClose();
      }}
      centered
      size="lg"
      dialogClassName="cookie-settings-modal"
    >
      <Modal.Header closeButton className="cookie-settings-modal__header">
        <Modal.Title style={{ fontSize: '20px', fontWeight: 'bold' }}>
          Cookie Settings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="cookie-settings-modal__body">
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '14px', color: '#333', marginBottom: '20px' }}>
            We use cookies and similar technologies to help personalize content, tailor and measure ads,
            and provide a better experience. We ask for your consent before using cookies that are not
            essential for the operation of our website. You can change your cookie preferences at any time
            through our Privacy Policy page.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#C93312', textDecoration: 'underline', fontSize: '14px' }}
            >
              Learn more about how we use cookies and your data rights
            </a>
          </div>

          <div className="cookie-category-list">
            {Object.entries(COOKIE_CATEGORIES).map(([category, data]) => (
              <CookieCategoryPanel
                key={category}
                category={category}
                data={data}
                enabled={preferences[category] || data.required}
                onToggle={handleToggleCategory}
                expanded={!!expandedCategories[category]}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="cookie-settings-modal__footer">
        <div>
          <Button
            variant="outline-danger"
            onClick={handleRejectAll}
            style={{
              borderColor: '#000',
              color: '#000',
              marginRight: '15px',
            }}
          >
            Reject Non-Essential Cookies
          </Button>
        </div>
        <div>
          <Button
            variant="outline-primary"
            className="cookie-settings-modal__btn-save"
            onClick={() => savePreferences(preferences)}
            style={{
              borderColor: '#C93312',
              color: '#C93312',
              marginRight: '10px',
            }}
          >
            Save Preferences
          </Button>
          <Button
            variant="primary"
            onClick={handleAcceptAll}
            style={{
              backgroundColor: '#C93312',
              borderColor: '#C93312',
            }}
          >
            Accept All
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CookiePreferences;
